import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Eye, Star, MapPin, Calendar, Activity, DollarSign } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6'];

export default function DashboardAnalytics() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/dashboard/analytics`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setStats(response.data.analytics);
            }
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
            // Mock data for demo
            setStats({
                totalAnalyses: 47,
                savedListings: 12,
                proprPoints: 250,
                avgScore: 72.5,
                recentActivity: [
                    { date: '2026-01-08', analyses: 5 },
                    { date: '2026-01-09', analyses: 8 },
                    { date: '2026-01-10', analyses: 3 },
                    { date: '2026-01-11', analyses: 7 },
                    { date: '2026-01-12', analyses: 6 },
                    { date: '2026-01-13', analyses: 9 },
                    { date: '2026-01-14', analyses: 9 }
                ],
                scoreDistribution: [
                    { range: '0-20', count: 2 },
                    { range: '21-40', count: 5 },
                    { range: '41-60', count: 12 },
                    { range: '61-80', count: 18 },
                    { range: '81-100', count: 10 }
                ],
                topRegions: [
                    { name: 'Lisboa', count: 20 },
                    { name: 'Porto', count: 12 },
                    { name: 'Algarve', count: 8 },
                    { name: 'Braga', count: 4 },
                    { name: 'Coimbra', count: 3 }
                ]
            });
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="relative w-16 h-16 mx-auto mb-4">
                        <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-t-emerald-600 rounded-full animate-spin"></div>
                    </div>
                    <p className="text-gray-600">Loading analytics...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                        <BarChart3 className="w-8 h-8 text-emerald-600" />
                        Analytics Dashboard
                    </h1>
                    <p className="text-gray-600">Your property analysis insights</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Total Analyses */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-2">
                            <div className="p-2 bg-emerald-100 rounded-lg">
                                <Activity className="w-6 h-6 text-emerald-600" />
                            </div>
                            <span className="text-xs text-gray-500">All Time</span>
                        </div>
                        <div className="text-3xl font-bold text-gray-900 mb-1">{stats.totalAnalyses}</div>
                        <div className="text-sm text-gray-600">Total Analyses</div>
                    </div>

                    {/* Saved Listings */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-2">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                                <Star className="w-6 h-6 text-yellow-600" />
                            </div>
                            <span className="text-xs text-gray-500">Saved</span>
                        </div>
                        <div className="text-3xl font-bold text-gray-900 mb-1">{stats.savedListings}</div>
                        <div className="text-sm text-gray-600">Saved Listings</div>
                    </div>

                    {/* ProprPoints */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-2">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <TrendingUp className="w-6 h-6 text-purple-600" />
                            </div>
                            <span className="text-xs text-gray-500">Earned</span>
                        </div>
                        <div className="text-3xl font-bold text-gray -900 mb-1">{stats.proprPoints}</div>
                        <div className="text-sm text-gray-600">ProprPoints</div>
                    </div>

                    {/* Average Score */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-2">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <BarChart3 className="w-6 h-6 text-blue-600" />
                            </div>
                            <span className="text-xs text-gray-500">Average</span>
                        </div>
                        <div className="text-3xl font-bold text-gray-900 mb-1">{stats.avgScore.toFixed(1)}</div>
                        <div className="text-sm text-gray-600">Avg Score</div>
                    </div>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Activity Chart */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-emerald-600" />
                            Recent Activity
                        </h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <AreaChart data={stats.recentActivity}>
                                <defs>
                                    <linearGradient id="colorAnalyses" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="date"
                                    stroke="#9ca3af"
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={(value) => new Date(value).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                                />
                                <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                    labelStyle={{ color: '#374151', fontWeight: 'bold' }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="analyses"
                                    stroke="#10b981"
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill="url(#colorAnalyses)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Score Distribution */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-emerald-600" />
                            Score Distribution
                        </h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={stats.scoreDistribution}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="range" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                                <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                    labelStyle={{ color: '#374151', fontWeight: 'bold' }}
                                />
                                <Bar dataKey="count" fill="#10b981" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Top Regions */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-emerald-600" />
                        Top Regions Analyzed
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        {stats.topRegions.map((region, index) => (
                            <div key={index} className="flex flex-col">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">{region.name}</span>
                                    <span className="text-2xl font-bold text-emerald-600">{region.count}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${(region.count / stats.topRegions[0].count) * 100}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
